import { useState } from 'react';
import { activePortalRegion, getPortalRegions, canAccessPortalRegion } from '../config/portal-regions';
import { useAuth } from '../contexts/AuthContext';
import { isInternalUser } from '../utils/internal-user';
import { Check, ChevronDown } from 'lucide-react';

const regionFlags: Record<string, string> = { us: '🇺🇸', au: '🇦🇺', ca: '🇨🇦', eu: '🇪🇺' };

export default function RegionSelector({ email: providedEmail, centerDropdown = false }: { email?: string; centerDropdown?: boolean } = {}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const email = user?.email || providedEmail;
  const canSwitchRegion = isInternalUser(email);
  const regions = getPortalRegions().filter(region => region.enabled)
    .sort((a, b) => a.label.localeCompare(b.label, 'en'));
  const activeRegion = regions.find(region => region.id === activePortalRegion.id) || activePortalRegion;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 hover:text-cyan-600 dark:text-gray-400 dark:hover:text-cyan-400 focus:outline-none transition-colors rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800"
        aria-label="Select your Simply Send region"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span aria-hidden="true">{regionFlags[activeRegion.id] || '🌐'}</span>
        <span>{activeRegion.label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute ${centerDropdown ? 'left-1/2 -translate-x-1/2' : 'right-0'} mt-2 w-64 rounded-lg shadow-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 z-50 py-1`} role="menu" aria-label="Select a region">
            {regions.map(region => {
              const comingSoon = region.id !== activePortalRegion.id;
              const selectionDisabled = comingSoon && (!canSwitchRegion || !canAccessPortalRegion(region, email));
              return (
              <button
                key={region.id}
                type="button"
                role="menuitem"
                disabled={selectionDisabled}
                onClick={() => {
                  if (selectionDisabled) return;
                  setOpen(false);
                  if (region.id !== activePortalRegion.id) window.location.assign(`${region.origin}/sign-in`);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-zinc-700 dark:text-zinc-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-zinc-100 dark:hover:bg-zinc-700/60 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-zinc-700 dark:disabled:hover:text-zinc-300"
                aria-current={region.id === activePortalRegion.id ? 'true' : undefined}
              >
                <span aria-hidden="true">{regionFlags[region.id] || '🌐'}</span>
                <span>{region.label}</span>
                {comingSoon && <span className="ml-auto text-[10px] whitespace-nowrap">Coming soon</span>}
                {region.id === activePortalRegion.id && <Check className="ml-auto h-3.5 w-3.5 text-cyan-700 dark:text-cyan-300" aria-hidden="true" />}
              </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
